import api from '../config/api';
import {
  AdminStatistics,
  AdminMemberListParams,
  AdminInstitutionListParams,
  RegistrationPetition,
  RegistrationPetitionDetail,
  PagedRegistrationPetitions,
  ReviewPetitionRequest,
  RegistrationApplicationStatus,
} from '../types/admin';
import { IndividualMemberDto, MemberVignetteRecord } from '../types/member';
import { InstitutionDto } from '../types/institution';
import { PagedResponse } from '../types/member';
import {
  MemberFilterParams,
  EmailCampaign,
  CreateEmailCampaign,
  EmailTemplate,
  CreateEmailTemplate,
  EmailSendResult,
  PagedEmailCampaigns,
  PagedEmailRecipients,
} from '../types/email';
import {
  ImportOptions,
  ExcelImportResponse,
  NormalizedRegisterImportResponse,
} from '../types/excelImport';
import {
  MemberPetition,
  PagedMemberPetitions,
  ReviewMemberPetitionRequest,
  PetitionType,
  MemberPetitionStatus,
} from '../types/petition';
import { normalizeMemberPetition } from '../utils/petition';

export const adminService = {
  getStatistics: async (): Promise<AdminStatistics> => {
    const response = await api.get<AdminStatistics>('/admin/statistics');
    return response.data;
  },

  getMembers: async (
    params: AdminMemberListParams = {}
  ): Promise<PagedResponse<IndividualMemberDto>> => {
    const response = await api.get<PagedResponse<IndividualMemberDto>>(
      '/admin/members',
      { params }
    );
    return response.data;
  },

  getInstitutions: async (
    params: AdminInstitutionListParams = {}
  ): Promise<PagedResponse<InstitutionDto>> => {
    const response = await api.get<PagedResponse<InstitutionDto>>(
      '/admin/institutions',
      { params }
    );
    return response.data;
  },

  getMemberById: async (id: string): Promise<IndividualMemberDto> => {
    const response = await api.get<IndividualMemberDto>(`/admin/members/${id}`);
    return response.data;
  },

  updateMember: async (id: string, data: Partial<IndividualMemberDto>): Promise<IndividualMemberDto> => {
    const response = await api.put<IndividualMemberDto>(`/admin/members/${id}`, data);
    return response.data;
  },

  createMember: async (data: FormData): Promise<IndividualMemberDto> => {
    const response = await api.post<IndividualMemberDto>('/admin/members', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  createInstitution: async (data: FormData): Promise<InstitutionDto> => {
    const response = await api.post<InstitutionDto>('/admin/institutions', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateVignette: async (
    id: string,
    data: {
      vignetteStatus: number;
      vignetteTrackingNumber?: string | null;
      year?: number;
      deliveredAt?: string | null;
    }
  ): Promise<IndividualMemberDto> => {
    const response = await api.put<IndividualMemberDto>(`/admin/members/${id}/vignette`, data);
    return response.data;
  },

  getMemberVignettes: async (id: string): Promise<MemberVignetteRecord[]> => {
    const response = await api.get<MemberVignetteRecord[]>(`/admin/members/${id}/vignettes`);
    return response.data;
  },

  getInstitutionById: async (id: string): Promise<InstitutionDto> => {
    const response = await api.get<InstitutionDto>(`/admin/institutions/${id}`);
    return response.data;
  },

  updateInstitution: async (id: string, data: Partial<InstitutionDto>): Promise<InstitutionDto> => {
    const response = await api.put<InstitutionDto>(`/admin/institutions/${id}`, data);
    return response.data;
  },

  addInstitutionCommittee: async (
    institutionId: string,
    data: Record<string, unknown>
  ): Promise<void> => {
    await api.post(`/admin/institutions/${institutionId}/committees`, data);
  },

  removeInstitutionCommittee: async (
    institutionId: string,
    institutionCommitteeId: string
  ): Promise<void> => {
    await api.delete(`/admin/institutions/${institutionId}/committees/${institutionCommitteeId}`);
  },

  deactivateMember: async (id: string): Promise<void> => {
    await api.post(`/admin/members/${id}/deactivate`);
  },

  reactivateMember: async (id: string): Promise<void> => {
    await api.post(`/admin/members/${id}/reactivate`);
  },

  deactivateInstitution: async (id: string): Promise<void> => {
    await api.post(`/admin/institutions/${id}/deactivate`);
  },

  reactivateInstitution: async (id: string): Promise<void> => {
    await api.post(`/admin/institutions/${id}/reactivate`);
  },

  // Email campaign methods
  getFilteredMembers: async (
    filters: MemberFilterParams
  ): Promise<PagedResponse<IndividualMemberDto>> => {
    const response = await api.post<PagedResponse<IndividualMemberDto>>(
      '/admin/email/members/filter',
      filters
    );
    return response.data;
  },

  createEmailCampaign: async (
    campaign: CreateEmailCampaign
  ): Promise<EmailCampaign> => {
    const response = await api.post<EmailCampaign>(
      '/admin/email/campaigns',
      campaign
    );
    return response.data;
  },

  getEmailCampaigns: async (
    page: number = 1,
    pageSize: number = 10
  ): Promise<PagedEmailCampaigns> => {
    const response = await api.get<PagedEmailCampaigns>(
      '/admin/email/campaigns',
      { params: { page, pageSize } }
    );
    return response.data;
  },

  getEmailCampaign: async (id: string): Promise<EmailCampaign> => {
    const response = await api.get<EmailCampaign>(`/admin/email/campaigns/${id}`);
    return response.data;
  },

  sendEmailCampaign: async (id: string): Promise<EmailSendResult> => {
    const response = await api.post<EmailSendResult>(
      `/admin/email/campaigns/${id}/send`
    );
    return response.data;
  },

  getCampaignRecipients: async (
    campaignId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<PagedEmailRecipients> => {
    const response = await api.get<PagedEmailRecipients>(
      `/admin/email/campaigns/${campaignId}/recipients`,
      { params: { page, pageSize } }
    );
    return response.data;
  },

  getEmailTemplates: async (): Promise<EmailTemplate[]> => {
    const response = await api.get<EmailTemplate[]>('/admin/email/templates');
    return response.data;
  },

  getEmailTemplate: async (id: string): Promise<EmailTemplate> => {
    const response = await api.get<EmailTemplate>(`/admin/email/templates/${id}`);
    return response.data;
  },

  saveEmailTemplate: async (
    template: CreateEmailTemplate
  ): Promise<EmailTemplate> => {
    const response = await api.post<EmailTemplate>(
      '/admin/email/templates',
      template
    );
    return response.data;
  },

  deleteEmailTemplate: async (id: string): Promise<void> => {
    await api.delete(`/admin/email/templates/${id}`);
  },

  getRegistrationPetitions: async (
    page: number = 1,
    pageSize: number = 20,
    status?: RegistrationApplicationStatus,
    type?: string
  ): Promise<PagedRegistrationPetitions> => {
    const response = await api.get<PagedRegistrationPetitions>(
      '/admin/registration-petitions',
      { params: { page, pageSize, status, type } }
    );
    return response.data;
  },

  getRegistrationPetitionDetail: async (id: string): Promise<RegistrationPetitionDetail> => {
    const response = await api.get<RegistrationPetitionDetail>(
      `/admin/registration-petitions/${id}`
    );
    return response.data;
  },

  downloadRegistrationDocument: async (documentId: string): Promise<Blob> => {
    const response = await api.get(
      `/admin/registration-petitions/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  reviewPetition: async (
    id: string,
    request: ReviewPetitionRequest
  ): Promise<void> => {
    await api.post(`/admin/registration-petitions/${id}/review`, request);
  },

  getMemberPetitions: async (
    page: number = 1,
    pageSize: number = 20,
    type?: PetitionType,
    status?: MemberPetitionStatus
  ): Promise<PagedMemberPetitions> => {
    const response = await api.get<PagedMemberPetitions>(
      '/admin/member-petitions',
      { params: { page, pageSize, type, status } }
    );
    const data = response.data;
    return {
      ...data,
      items: (data.items ?? []).map(normalizeMemberPetition),
    };
  },

  reviewMemberPetition: async (
    id: string,
    request: ReviewMemberPetitionRequest
  ): Promise<void> => {
    await api.post(`/admin/member-petitions/${id}/review`, request);
  },

  // Excel import methods
  importMembers: async (
    file: File,
    options: ImportOptions
  ): Promise<ExcelImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Append options with proper naming for ASP.NET Core model binding
    formData.append('SkipDuplicates', options.skipDuplicates.toString());
    formData.append('UpdateExisting', options.updateExisting.toString());
    formData.append('CreateUserAccounts', options.createUserAccounts.toString());
    if (options.defaultRegistrationYear) {
      formData.append('DefaultRegistrationYear', options.defaultRegistrationYear.toString());
    }
    if (options.defaultMembershipType !== undefined) {
      formData.append('DefaultMembershipType', options.defaultMembershipType.toString());
    }
    if (options.useCatalogFormat) {
      formData.append('UseCatalogFormat', options.useCatalogFormat.toString());
    }

    const response = await api.post<ExcelImportResponse>(
      '/admin/members/import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  downloadNormalizedImportTemplate: async (
    memberType: 'Individual' | 'Organization' = 'Individual'
  ): Promise<Blob> => {
    const response = await api.get('/admin/members/import-normalized/template', {
      params: { memberType },
      responseType: 'blob',
    });
    return response.data;
  },

  importNormalizedRegister: async (
    file: File,
    memberType: 'Individual' | 'Organization'
  ): Promise<NormalizedRegisterImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('memberType', memberType);

    const response = await api.post<NormalizedRegisterImportResponse>(
      '/admin/members/import-normalized',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 10 * 60 * 1000, // large register import against remote SQL
      }
    );
    return response.data;
  },
};
